import {
  axiosInstance,
  getConfiguration,
  Team,
  TeamRole,
} from '@foxtrotplatform/platform-micro-site-core';
import { AxiosRequestConfig } from 'axios';

import { DeveloperAppConfig } from '../../common/config/developerAppConfig';
import { ApiResponse, Invitation } from './types';

export const updateTeam = async (
  orgId: string,
  teamId: string,
  teamName: string
): Promise<void> => {
  const config = (await getConfiguration()) as DeveloperAppConfig;
  const request: AxiosRequestConfig = {
    url: `${config.baseApiUrl}/organization/${orgId}/team/${teamId}`,
    method: 'put',
    data: { teamName },
  };
  await axiosInstance(request);
};

export const inviteUser = async (
  orgId: string,
  teamId: string,
  email: string,
  role: TeamRole
): Promise<void> => {
  const config = (await getConfiguration()) as DeveloperAppConfig;
  const request: AxiosRequestConfig = {
    url: `${config.baseApiUrl}/organization/${orgId}/team/${teamId}/invite`,
    method: 'post',
    data: {
      emailId: email,
      role,
    },
  };
  await axiosInstance(request);
};

export const deleteInvitation = async (
  orgId: string,
  teamId: string,
  email: string,
  inviteId: string
): Promise<void> => {
  const config = (await getConfiguration()) as DeveloperAppConfig;
  const request: AxiosRequestConfig = {
    url: `${config.baseApiUrl}/organization/${orgId}/team/${teamId}/invite/delete`,
    method: 'put',
    data: {
      id: inviteId,
      emailId: email,
    },
  };
  await axiosInstance(request);
};

export const readTeamMembers = async (
  orgId: string,
  teamId: string
): Promise<TeamMember[]> => {
  const config = (await getConfiguration()) as DeveloperAppConfig;
  const request: AxiosRequestConfig = {
    url: `${config.baseApiUrl}/organization/${orgId}/team/${teamId}/members`,
    method: 'get',
  };
  const response: ApiResponse<any> = await axiosInstance(request);

  const teamMembers: TeamMember[] = response.data.map(
    (x: {
      userId: string;
      emailId: string;
      userRoleName: string;
      userFullName: string;
    }) => {
      return {
        id: x.userId,
        email: x.emailId,
        role: x.userRoleName,
        fullName: x.userFullName,
      };
    }
  );

  return teamMembers;
};

export const updateMemberRole = async (
  orgId: string,
  teamId: string,
  userId: string,
  role: TeamRole
): Promise<void> => {
  const config = (await getConfiguration()) as DeveloperAppConfig;
  const request: AxiosRequestConfig = {
    url: `${config.baseApiUrl}/organization/${orgId}/team/${teamId}/members/${userId}`,
    method: 'put',
    data: {
      role,
    },
  };
  await axiosInstance(request);
};

export const removeFromTeam = async (
  orgId: string,
  teamId: string,
  userId: string
): Promise<void> => {
  const config = (await getConfiguration()) as DeveloperAppConfig;
  const request: AxiosRequestConfig = {
    url: `${config.baseApiUrl}/organization/${orgId}/team/${teamId}/members/${userId}`,
    method: 'delete',
  };
  await axiosInstance(request);
};

export const acceptTeamInvitation = async (
  orgId: string,
  teamId: string,
  email: string
): Promise<void> => {
  const config = (await getConfiguration()) as DeveloperAppConfig;
  const request: AxiosRequestConfig = {
    url: `${config.baseApiUrl}/organization/${orgId}/team/${teamId}/invite/accept`,
    method: 'put',
    data: {
      emailId: email,
    },
  };
  await axiosInstance(request);
};

export const declineTeamInvitation = async (
  orgId: string,
  teamId: string,
  email: string,
  id: string
): Promise<void> => {
  const config = (await getConfiguration()) as DeveloperAppConfig;
  const request: AxiosRequestConfig = {
    url: `${config.baseApiUrl}/organization/${orgId}/team/${teamId}/invite/delete`,
    method: 'put',
    data: {
      id,
      emailId: email,
    },
  };
  await axiosInstance(request);
};

export const approveUserTeamRequest = async (
  orgId: string,
  teamId: string,
  email: string
): Promise<void> => {
  const config = (await getConfiguration()) as DeveloperAppConfig;
  const request: AxiosRequestConfig = {
    url: `${config.baseApiUrl}/organization/${orgId}/team/${teamId}/join/accept`,
    method: 'put',
    data: {
      emailId: email,
    },
  };
  await axiosInstance(request);
};

export const denyUserTeamRequest = async (
  orgId: string,
  teamId: string,
  email: string,
  id: string
): Promise<void> => {
  const config = (await getConfiguration()) as DeveloperAppConfig;
  const request: AxiosRequestConfig = {
    url: `${config.baseApiUrl}/organization/${orgId}/team/${teamId}/join/deny`,
    method: 'put',
    data: {
      id,
      emailId: email,
    },
  };
  await axiosInstance(request);
};

export const createTeam = async (
  orgId: string,
  teamName: string
): Promise<string> => {
  const config = (await getConfiguration()) as DeveloperAppConfig;
  const request: AxiosRequestConfig = {
    url: `${config.baseApiUrl}/organization/${orgId}/team`,
    method: 'post',
    data: {
      teamName,
    },
  };
  const response: ApiResponse<Team> = await axiosInstance(request);
  return response.data.id;
};

export const requestToJoin = async (
  orgId: string,
  teamId: string
): Promise<void> => {
  const config = (await getConfiguration()) as DeveloperAppConfig;
  const request: AxiosRequestConfig = {
    url: `${config.baseApiUrl}/organization/${orgId}/team/${teamId}/join`,
    method: 'post',
  };
  await axiosInstance(request);
};

export const readInvitations = async (
  orgId: string,
  teamId: string
): Promise<Invitation[]> => {
  const config = (await getConfiguration()) as DeveloperAppConfig;
  const request: AxiosRequestConfig = {
    url: `${config.baseApiUrl}/organization/${orgId}/team/${teamId}/invitations`,
    method: 'get',
  };
  const response: ApiResponse<Invitation[]> = await axiosInstance(request);

  return response.data;
};
